import {
  getActiveOptions,
  isOptionActive,
  MobileProduct,
  Option,
  Product,
} from '@dch/products-business-logic';
import {
  IOrderingInput,
  IOrderingInputVariableQuery,
  OrderingStatusDataModel,
} from '../models/ebu-ordering.model';
import { TranslationService } from '@proximus/ng-common';

export const buildCancelled = () => ({
  status: OrderingStatusDataModel.CANCELED,
});

export const buildDefaultError = (translationService: TranslationService) => ({
  status: OrderingStatusDataModel.ERROR,
  error: {
    message: translationService.getTranslatedValueFor(
      'ebu.ordering.genericError',
    ),
  },
});

export const buildDefaultSuccess = (
  option: Option | undefined,
  translationService: TranslationService,
) => {
  if (option) {
    if (isOptionActive(option)) {
      return buildDefaultSuccessCease(translationService);
    }
    return buildDefaultSuccessOrder(translationService);
  }

  return {
    status: OrderingStatusDataModel.SUCCESS,
    info: {
      message: translationService.getTranslatedValueFor(
        'ebu.ordering.genericSuccess',
      ),
    },
  };
};

export const buildDefaultSuccessCease = (
  translationService: TranslationService,
) => ({
  status: OrderingStatusDataModel.SUCCESS,
  info: {
    message: translationService.getTranslatedValueFor(
      'ebu.ordering.success.cease',
    ),
  },
});

export const buildDefaultSuccessOrder = (
  translationService: TranslationService,
) => ({
  status: OrderingStatusDataModel.SUCCESS,
  info: {
    message: translationService.getTranslatedValueFor(
      'ebu.ordering.success.order',
    ),
  },
});

export const getProductUuid = (product?: MobileProduct) => {
  if (product && product.accessNumber) {
    return `${product.accessNumber}.${product.plc}`;
  }
  return '';
};

export const hasIncompatibleOptions = (
  orderingInput: IOrderingInput,
): boolean => orderingInput.hasIncompatibleOptions;

export const getOrderingInput = (
  product: Product,
  option: Option,
  translationService: TranslationService,
) => {
  const activeOptions = getActiveOptions(product);
  const allActionsToCease: { productId: string; action: string }[] = [];
  const incompatibleOptions: Array<string> = [];
  if (!isOptionActive(option)) {
    activeOptions.forEach((activeOption: Option) => {
      const actionToCease: any = option.incompatibleWith?.find(
        (incompatibleOption: any) =>
          incompatibleOption.pniCode === activeOption.optionCodeForDescription,
      );
      if (actionToCease) {
        allActionsToCease.push({
          productId: actionToCease.pniCode,
          action: 'CEASE',
        });
        incompatibleOptions.push(activeOption.name);
      }
    });
  }
  return {
    orderingInput: {
      channel: 'MYAPP',
      flow: option?.categories?.includes('OSD')
        ? 'NATIVE_ONESHOT'
        : 'NATIVE_OPTIONS',
      products: [
        {
          productId: 'DUMMY',
          action: 'KEEP',
          properties: {
            serviceId: product.accessNumber,
          },
          products: [
            {
              productId: option.optionCodeForDescription,
              action: !isOptionActive(option) ? 'PROVIDE' : 'CEASE',
            },
          ].concat(allActionsToCease),
        },
      ],
    },
    hasIncompatibleOptions: allActionsToCease && allActionsToCease.length > 0,
    incompatibleOptions: allActionsToCease,
    validation: {
      description: getTranslationWithPlaceholders(
        translationService,
        'ebu.incompatibleOptions.popup.description',
        option.name,
        incompatibleOptions.join(','),
      ),
      question: getTranslationWithPlaceholders(
        translationService,
        'ebu.incompatibleOptions.popup.question',
        option.name,
        incompatibleOptions.join(','),
      ),
    },
  };
};

const getTranslationWithPlaceholders = (
  translationService: TranslationService,
  key: string,
  optionToOrder: string,
  optionToCease: string,
) => {
  return translationService
    .getTranslatedValueFor(key)
    .replace(/\{0\}/g, optionToOrder)
    .replace(/\{1\}/g, optionToCease);
};

export const extractOrderingInput = (
  orderingInput: IOrderingInput,
): IOrderingInputVariableQuery => orderingInput.orderingInput;

export function isOSDOption(option: Option): boolean {
  return option.categories.some((category: string) => category === 'OSD');
}
